/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useCallback, useEffect, useMemo, useReducer } from 'react';
import MultimediaContext from './MultimediaContext';
import initialMultimediaState from './initialMultimediaState';
import userReducer from 'contexts/multimedia/reducer/multimedia.reducer';
import * as action from 'contexts/multimedia/reducer/multimedia.actions';
import { useTranslation } from 'react-i18next';
import { IMyBrand } from 'interfaces/myBrands.interface';
import { useAuth0 } from '@auth0/auth0-react';
import {
  MultimediaTypes,
  TInitialMultimediaState,
  TMediaState,
} from 'interfaces/multimedia.interface';
import { abortCatalogController } from 'utils/abortController';
import useContextMyBrands from 'hooks/contexts/useContextMyBrands';
import * as MTypes from './reducer/multimedia.types';
import { ChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';

function MultimediaProvider(props: ChildrenProps) {
  const [mma, dispatch] = useReducer(userReducer, initialMultimediaState);
  const multimedia = mma as TInitialMultimediaState;
  const { connectedBrands, isLoadingGetMyBrands } = useContextMyBrands();
  const { company } = useContextCompany();
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();
  const myBrandsIds = useMemo(
    () => connectedBrands.map((item) => item.tenant.brand.id),
    [connectedBrands]
  );

  useEffect(() => {
    if (!company?.id) return;
    dispatch({ type: MTypes.RESET_MEDIA });
  }, [company?.id]);

  const getMedia = useCallback(
    async (type: MultimediaTypes) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      !isLoadingGetMyBrands &&
        action.getMediaAction(
          dispatch,
          multimedia[type as keyof typeof multimedia] as TMediaState,
          myBrandsIds,
          type,
          signal,
          translate,
          token
        );
    },
    [myBrandsIds, isLoadingGetMyBrands, multimedia]
  );

  const getMoreMedia = useCallback(
    async (type: MultimediaTypes) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      myBrandsIds.length > 0 &&
        action.getMoreMediaAction(
          dispatch,
          multimedia[type as keyof typeof multimedia] as TMediaState,
          myBrandsIds,
          type,
          signal,
          translate,
          token
        );
    },
    [myBrandsIds, multimedia]
  );

  const getVideosByBrand = useCallback(async (brandId: string) => {
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    action.getVideosByBrandAction(dispatch, brandId, signal, translate, token);
  }, []);

  const searchMedia = useCallback(
    async (query: string, type: MultimediaTypes) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      action.searchMediaAction(
        dispatch,
        multimedia[type as keyof typeof multimedia] as TMediaState,
        query,
        type,
        signal,
        translate,
        token
      );
    },
    [multimedia]
  );

  const setBrandSelected = useCallback(
    async (brand: IMyBrand, type: MultimediaTypes) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      myBrandsIds.length > 0 &&
        action.setBrandSelectedAction(
          dispatch,
          brand,
          myBrandsIds,
          type,
          multimedia[type as keyof typeof multimedia] as TMediaState,
          signal,
          translate,
          token
        );
    },
    [multimedia, myBrandsIds]
  );

  const memoProvider = useMemo(
    () => ({
      ...multimedia,
      getMedia,
      getMoreMedia,
      getVideosByBrand,
      searchMedia,
      setBrandSelected,
    }),
    [multimedia, getMedia, getMoreMedia, getVideosByBrand, searchMedia, setBrandSelected]
  );

  return (
    <MultimediaContext.Provider value={memoProvider}>
      {props.children}
    </MultimediaContext.Provider>
  );
}

export default memo(MultimediaProvider);
